import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/lib/api';
import { Badge, Button, Card, PageHeader } from '@/components/ui';
import { CertificateDownloadButton, LinkedInActions } from '@/components/certificate-download';
import { formatCertificateDate } from '@/lib/dates';

export function CertificatesPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const certs = useQuery({ queryKey: ['certificates'], queryFn: api.certificates });
  const toggle = useMutation({
    mutationFn: ({ id, isPublic }: { id: string; isPublic: boolean }) =>
      api.setCertificatePublic(id, isPublic),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certificates'] }),
  });

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Your certificates"
        description="Download your certificates, or share public ones on LinkedIn so anyone can verify them."
        back={{ to: '/home', label: 'Home' }}
      />
      {certs.data?.certificates.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            Pass a quiz with a certificate to earn your first one.
          </p>
        </Card>
      )}
      <div className="space-y-3">
        {certs.data?.certificates.map((c) => (
          <Card key={c.id} className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                {c.isPublic ? (
                  <Link to={`/verify/${c.code}`} className="font-medium hover:text-primary">
                    {c.title}
                  </Link>
                ) : (
                  <p className="font-medium">{c.title}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  {c.issuerName} · {c.percent}% · {formatCertificateDate(c.issuedAt, c.timeZone)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge>{c.isPublic ? 'Public' : 'Private'}</Badge>
                <Button
                  variant="ghost"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ id: c.id, isPublic: !c.isPublic })}
                >
                  {c.isPublic ? 'Make private' : 'Make public'}
                </Button>
              </div>
            </div>
            {c.isPublic ? (
              <LinkedInActions cert={c} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Make this certificate public to share it on LinkedIn.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <CertificateDownloadButton code={c.code} format="pdf" />
              <CertificateDownloadButton code={c.code} format="png" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
